import {
  TAttendanceByDateResponse,
  TAttendanceByUserResponse,
} from '../__shared/attendance.interfaces';
import { normalizePaginatedResponse } from '../__shared/paginationUtils';
import { TPaginationParams, TUserAction } from '../__shared/shared.interfaces';
import {
  TStaffResponse,
  TStaffCreationPayload,
  TStaffUpdatePayload,
  TStaffListApiResponse,
  TStaffListParams,
  TStaffDetailsResponse,
  EMPLOYMENT_STATUS,
} from './staff.interfaces';
import { TDocument } from '../documents/documents.interfaces';
import { mockStaffDocuments } from '../../__mocks__/staff/staffDocumentsMock';
import { createHttpService } from '../ApiService';
import { mockStaffDetails } from '../../__mocks__/staff/staffDetailsMock';
import { mockStaffList } from '../../__mocks__/staff/staffListMock';
import {
  IStaffPayout,
  IStaffPayoutResponse,
} from './staffPayout.interfaces';
import {
  IApplyLeavePayload,
  ILeaveRecord,
  ILeaveStatsResponse,
} from './staffLeaves.interfaces';
import { mockStaffPayoutList } from '../../__mocks__/staff/staffPayoutMock';
import {
  mockStaffLeaveStats,
  mockStaffLeaveList,
} from '../../__mocks__/staff/staffLeavesMock';
import { mockAttendanceByDateEntries } from '../../__mocks__/__common/attendanceByDate.mock';
import { mockAttendanceByUser } from '../../__mocks__/__common/attendancebyUser.mock';
import { Dayjs } from 'dayjs';
import { useRbacState } from '../../context/rbac.context';
import { checkApiPermission } from '../../utils/rbacHelpers';
import isAppWithMock from 'src/utils/isAppWithMock';

export const useStaffService = () => {
  const httpAPI = createHttpService();
  const isMockData = isAppWithMock();
  const { rbac } = useRbacState();

  const getStaffList = (
    _params: TStaffListParams
  ): Promise<TStaffListApiResponse> => {
    if (isMockData) {
      return Promise.resolve(
        normalizePaginatedResponse<TStaffResponse>(
          { data: mockStaffList, count: mockStaffList.length, page: 1, limit: 10 },
          'data'
        )
      );
    }
    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 10,
        ...(_params.search && { search: _params.search }),
        ...(_params.department && { department: _params.department }),
        ...(_params.designation && { designation: _params.designation }),
        ...(_params.employmentStatus && { employmentStatus: _params.employmentStatus }),
      },
    };

    return httpAPI.staff
      .get<any>('', requestObj)
      .then((res) => {
        // processReponse in HttpBaseApiWrapper already unwraps axios response.data,
        // so res IS the BE body: { data: [...], count: N, page: N, limit: N }
        const body = res as any;
        const dataArray = Array.isArray(body) ? body : (body?.data ?? []);
        if (Array.isArray(dataArray)) {
          const mapped: TStaffResponse[] = dataArray.map((item: any) => {
            const createdAction: TUserAction = {
              userId: item.createdBy || '',
              timestamp: item.createdAt
                ? new Date(item.createdAt).getTime()
                : Date.now(),
            };
            const employmentStatusValue = (item.employmentStatus || '').toString().toLowerCase();
            const employmentStatus: EMPLOYMENT_STATUS =
              Object.values(EMPLOYMENT_STATUS).includes(employmentStatusValue as EMPLOYMENT_STATUS)
                ? (employmentStatusValue as EMPLOYMENT_STATUS)
                : EMPLOYMENT_STATUS.ACTIVE;

            return {
              id: item.id,
              basicDetails: item.basicDetails || {
                firstName: '',
                lastName: '',
                middleName: null,
                gender: null,
              },
              actions: { created: createdAction },
              contactDetails: item.contactDetails || {
                phone: { countryCode: '', phoneNumber: '', isVerified: false },
                email: { email: '', isVerified: false },
                address: item.addressDetails
                  ? {
                    _id: item.addressDetails._id,
                    address1: item.addressDetails.street1 || '',
                    address2: item.addressDetails.street2 || null,
                    city: item.addressDetails.city || '',
                    state: item.addressDetails.state || null,
                    country: null,
                    postalCode: item.addressDetails.pinCode || '',
                    isCurrent: item.addressDetails.isCurrent || null,
                    description: item.addressDetails.description || null,
                  }
                  : undefined,
              },
              documents: [],
              designation: item.designation || '',
              department: item.department || '',
              joiningDate: {
                userId: item.createdBy || '',
                timestamp: item.joiningDate
                  ? new Date(item.joiningDate).getTime()
                  : Date.now(),
              },
              employmentStatus,
              email: item.contactDetails?.email?.email || '',
            } as TStaffResponse;
          });
          return normalizePaginatedResponse<TStaffResponse>(
            { ...body, data: mapped },
            'data'
          );
        }
        return normalizePaginatedResponse<TStaffResponse>({}, 'data');
      })
      .catch(() => normalizePaginatedResponse<TStaffResponse>({}, 'data'));
  };
  const getStaffById = (staffId: string): Promise<TStaffDetailsResponse> => {
    if (isMockData) {
      return Promise.resolve(mockStaffDetails);
    }
    return httpAPI.staff.get<TStaffDetailsResponse>(`/by-id/${staffId}`);
  };
  const createStaff = (
    _payload: TStaffCreationPayload
  ): Promise<TStaffResponse> => {
    checkApiPermission(rbac, 'staff', 'staffList', 'write');
    return httpAPI.staff.post<TStaffResponse>('', _payload);
  };

  const getStaffAttendanceByDate = (
    _params: TPaginationParams,
    dateRange: { fromDate: Dayjs | null; toDate: Dayjs | null }
  ): Promise<TAttendanceByDateResponse> => {
    if (isMockData) {
      return Promise.resolve(mockAttendanceByDateEntries);
    }
    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 10,
      },
    };
    return httpAPI.staff
      .get<any>('/attendance/by-date', {
        params: {
          ...requestObj.params,
          fromDate: dateRange.fromDate?.format('YYYY-MM-DD') || '',
          toDate: dateRange.toDate?.format('YYYY-MM-DD') || '',
        },
      })
      .then((response) => {
        return (response.data || response) as TAttendanceByDateResponse;
      })
      .catch(() => {
        return [] as TAttendanceByDateResponse;
      });
  };

  const getStaffAttendanceByUser = (
    _params: TPaginationParams,
    dateRange: { fromDate: Dayjs | null; toDate: Dayjs | null },
    staffId?: string
  ): Promise<TAttendanceByUserResponse> => {
    if (isMockData) {
      const userId = mockAttendanceByUser.userDetails.id || 'user-1';
      return Promise.resolve({
        [userId]: mockAttendanceByUser,
      });
    }
    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 10,
        ...(staffId && { staffId }),
      },
    };
    return httpAPI.staff
      .get<any>('/attendance/by-user', {
        params: {
          ...requestObj.params,
          fromDate: dateRange.fromDate?.format('YYYY-MM-DD') || '',
          toDate: dateRange.toDate?.format('YYYY-MM-DD') || '',
        },
      })
      .then((response) => {
        return (response.data || response) as TAttendanceByUserResponse;
      })
      .catch(() => {
        return {} as TAttendanceByUserResponse;
      });
  };

  // Legacy method for backward compatibility
  const getStaffAttendance = (
    _params: TPaginationParams,
    dateRange: { fromDate: Dayjs | null; toDate: Dayjs | null }
  ): Promise<TAttendanceByDateResponse> => {
    if (isMockData) {
      return Promise.resolve(mockAttendanceByDateEntries);
    }
    return getStaffAttendanceByDate(_params, dateRange);
  };

  const getStaffPayouts = (
    _params: TPaginationParams
  ): Promise<IStaffPayoutResponse> => {
    if (isMockData) {
      return Promise.resolve(
        normalizePaginatedResponse<IStaffPayout>(
          {
            data: mockStaffPayoutList,
            count: mockStaffPayoutList.length,
            page: _params.page ?? 1,
            limit: _params.limit ?? 10,
          },
          'data'
        )
      );
    }
    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 10,
      },
    };
    return httpAPI.staff
      .get<Record<string, unknown>>('/payouts', requestObj)
      .then((res) => {
        const body = (res?.data ?? res) as Record<string, unknown>;
        return normalizePaginatedResponse<IStaffPayout>(body ?? {}, 'data');
      })
      .catch(() =>
        normalizePaginatedResponse<IStaffPayout>({ data: [] }, 'data')
      );
  };

  const getStaffDocuments = (
    _params: TPaginationParams,
    staffUserId?: string
  ): Promise<TDocument[]> => {
    if (isMockData || !staffUserId) {
      return Promise.resolve(mockStaffDocuments);
    }
    return httpAPI.documents
      .get<any>('', {
        params: {
          page: _params.page || 1,
          limit: _params.limit || 20,
          ownerType: 'user',
          userId: staffUserId,
        },
      })
      .then((res: any) => {
        const body = res?.data ?? res;
        return Array.isArray(body?.data) ? body.data : body ?? [];
      })
      .catch(() => mockStaffDocuments);
  };

  const postStaffDocument = (_payload: TDocument): Promise<TDocument> => {
    checkApiPermission(rbac, 'staff', 'documents', 'write');
    if (isMockData) return Promise.resolve(mockStaffDocuments[0]);
    return httpAPI.documents
      .post<TDocument>('', _payload)
      .then((res: any) => res?.data ?? res);
  };

  const patchStaffDocument = (_payload: TDocument): Promise<TDocument> => {
    checkApiPermission(rbac, 'staff', 'documents', 'write');
    if (isMockData) return Promise.resolve(mockStaffDocuments[0]);
    const docId = (_payload as any).id || (_payload as any)._id;
    return httpAPI.documents
      .put<TDocument>(`/${docId}`, _payload)
      .then((res: any) => res?.data ?? res);
  };

  const getStaffLeaveStats = (staffId?: string): Promise<ILeaveStatsResponse> => {
    if (isMockData) return Promise.resolve(mockStaffLeaveStats);
    return httpAPI.staff
      .get<ILeaveStatsResponse>('/leaves/stats', {
        params: { ...(staffId && { staffId }) },
      })
      .then((res: any) => res?.data ?? res ?? { stats: [] });
  };

  const getStaffLeaves = (_params: TPaginationParams): Promise<ILeaveRecord[]> => {
    if (isMockData) return Promise.resolve(mockStaffLeaveList);
    return httpAPI.staff
      .get<any>('/leaves', {
        params: { page: _params.page, limit: _params.limit },
      })
      .then((res: any) => {
        const body = res?.data ?? res;
        return Array.isArray(body?.data) ? body.data : body ?? [];
      });
  };

  const applyStaffLeave = (payload: IApplyLeavePayload): Promise<ILeaveRecord> => {
    checkApiPermission(rbac, 'staff', 'staffList', 'write');
    if (isMockData) {
      return Promise.resolve({
        id: 'leave-new',
        leaveTypeId: payload.leaveTypeId,
        leaveTypeName: 'Leave',
        leaveTypeCode: 'paid',
        startDate: payload.startDate,
        endDate: payload.endDate,
        status: 'pending',
        reason: payload.reason,
        appliedAt: new Date().toISOString(),
      });
    }
    return httpAPI.staff
      .post<ILeaveRecord>('/leaves', payload)
      .then((res: any) => res?.data ?? res);
  };

  const updateStaff = (
    staffId: string,
    payload: TStaffUpdatePayload
  ): Promise<TStaffDetailsResponse> => {
    checkApiPermission(rbac, 'staff', 'staffList', 'write');
    return httpAPI.staff.put<TStaffDetailsResponse>(`/${staffId}`, payload);
  };

  const deleteStaff = (staffId: string): Promise<{ message: string; id: string }> => {
    checkApiPermission(rbac, 'staff', 'staffList', 'delete');
    return httpAPI.staff.delete<{ message: string; id: string }>(`/${staffId}`);
  };

  return {
    getStaffById,
    createStaff,
    updateStaff,
    deleteStaff,
    getStaffList,
    getStaffAttendance,
    getStaffAttendanceByDate,
    getStaffAttendanceByUser,
    getStaffPayouts,
    getStaffDocuments,
    postStaffDocument,
    patchStaffDocument,
    getStaffLeaveStats,
    getStaffLeaves,
    applyStaffLeave,
  };
};
